import React, { useState, useEffect } from "react";
import { useSearchParams, Navigate } from "react-router-dom";
import { Card, Row, Col, Button } from "react-bootstrap";
import { IActionHandler, WebsocketCryptoMessage, MessageDisplayType } from "@preveil-api";
import {
  useAppSelector, useAppDispatch, QueryParamKeys, Message, normalizeQueryUserId, AccountWSSteps, DefaultRoutes, QueryParamValues, AccountErrorMessages, MessageHandlerDisplayType,
  GlobalErrorMessages, MessageAnchors, ConfigKeys
} from "src/common";
import { Loading } from "src/components";
import { DefaultPanel, AwaitConnection, ConnectionEstablished, ErrorPanel } from ".";
import { uiActions, websocketActions, WebsocketStatus, getConfigValue } from "src/store";
import { RootState } from "src/store/configureStore";
import CopyAccountImage from "src/assets/images/copy_account.svg";

function CopyAccountComponent() {
  const [search_params, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const message = useAppSelector((state: RootState) => state.websocket.message as WebsocketCryptoMessage);
  const status = useAppSelector((state: RootState) => state.websocket.status);
  const errors = useAppSelector((state: RootState) => state.websocket.errors);
  const [user_id, setUserId] = useState<string>();
  const [step, setStep] = useState<number>(AccountWSSteps.DISCONNECTED);
  const [pin, setPin] = useState<string>();
  const crypto_websocket_server = getConfigValue(ConfigKeys.crypto_websocket_server);

  // Description: load accounts and current account from crypto (First onload event)
  useEffect(() => {
    handleInitCopyAccount();
    return () => {  // Destroy websocket State on leave
      dispatch(websocketActions.destroyWebsocket());
    };
  }, []);

  // Description: Responds to changes in message and websocket status
  useEffect(() => {
    const is_error = status === WebsocketStatus.error || message?.status === WebsocketStatus.error;
    let next = is_error ? AccountWSSteps.ERROR : !!message ? step + 1 : step;

    if (!!message?.pin) {
      setPin(message.pin);
      next = AccountWSSteps.GET_CONNECTION_ESTABLISHED;
    }

    setStep(next);
  }, [message, status]);


  // Description: load accounts and current account from crypto (First onload event)
  useEffect(() => {
    !!errors &&
      CopyAccountRequests.handlePageErrorMessage({ message: AccountErrorMessages.default, stack: errors });
  }, [errors]);


  /* 
  *   Description: Initialize Component:
  *            - Get user ID from QS and pass it to the email input
  *            - Sets up KeyExchangeManager Instance
  */
  function handleInitCopyAccount() {
    const query = search_params.get(QueryParamKeys.USER_ID_QUERY_KEY);
    const query_user_id = normalizeQueryUserId(query);
    !!query_user_id && setUserId(query_user_id);
    if (!!query) {
      search_params.delete(QueryParamKeys.USER_ID_QUERY_KEY);
      setSearchParams(search_params);
    }
  }

  // Description: Allow user to reset form if ws gets stuck or connection is disrupted
  function InPlaceLoading() {
    return <>
      <Loading className="in-place" />
      <Button onClick={() => CopyAccountRequests.handleDestroy()} >Try again</Button>
    </>;
  }

  // Description: Render panels by steps
  const CopyAccountPanels = () => {
    switch (step) {
      case AccountWSSteps.ERROR:
        return <ErrorPanel handleAction={handlePageActions} />;
      case AccountWSSteps.DISCONNECTED:
        return <DefaultPanel handleAction={handlePageActions} user_id={user_id} />;
      case AccountWSSteps.INITIALIZE_CONNECTION:
      case AccountWSSteps.GET_ACCEPTED_INIT_OK:
        return <AwaitConnection handleAction={handlePageActions} />;
      case AccountWSSteps.GET_CONNECTION_ESTABLISHED:
        return !!pin ? <ConnectionEstablished pin={pin} handleAction={handlePageActions} /> : <InPlaceLoading />;
      case AccountWSSteps.GET_KEY_TRANSFER_COMPLETE:
        return <Navigate to={`${DefaultRoutes.mail_default}?user_id=${!!user_id ? encodeURIComponent(user_id) : ""}&${QueryParamKeys.PAGE_ACTION_QUERY_KEY}=${QueryParamValues.added}`} />;
      default:
        return <InPlaceLoading />;
    };
  };

  // Description: Page actions
  const CopyAccountRequests = {
    // Description: Initialize the connection
    handleInitWebsocketConnection: (params: { user_id: string }) => {
      const _user_id = params.user_id;
      if (!!_user_id) {
        const host = `${crypto_websocket_server}/get/account/${encodeURIComponent(_user_id)}/key/get`;
        dispatch(websocketActions.wsConnect({ host, init: { action: "init" } }));
        setUserId(_user_id);
      };
    },

    // Description: Reset message state and ws connection
    handleResetMessage: () => {
      dispatch(websocketActions.wsOnResetMessage());
    },

    // Description: Reset   ws connection
    handleDisconnect: () => {
      dispatch(websocketActions.wsDisconnect());
    },

    // Description: Reset page state and ws connection
    handleDestroy: () => {
      setStep(AccountWSSteps.DISCONNECTED);
      setUserId(undefined);
      dispatch(websocketActions.destroyWebsocket());
    },

    handlePageError: (params: { message: string, stack?: any, displayType?: MessageDisplayType }) => {
      const display_type = params.displayType || MessageHandlerDisplayType.toastr;
      dispatch(uiActions.handleRequestErrors(new Message(params.message, display_type), params.stack));
    },

    handlePageErrorMessage: (params: { message: string, stack?: any }) => {
      dispatch(uiActions.handleRequestErrors(new Message(params.message, MessageHandlerDisplayType.logger), params.stack));
    }
  };

  //  Description: Handle all actions from Children forms
  function handlePageActions(action_obj: IActionHandler) {
    const callback = `handle${action_obj.actionType}`;
    if ((CopyAccountRequests as any)[callback] instanceof Function) {
      (CopyAccountRequests as any)[callback](action_obj.params);
    } else {
      const message = GlobalErrorMessages.no_handler_found.replace(MessageAnchors.actionType, action_obj.actionType);
      CopyAccountRequests.handlePageErrorMessage({ message, stack: action_obj });
    }
  }

  return <Row className="justify-content-center">
    <Col md={8}>
      <h1 className="main-header">Copy account from computer or phone</h1>
      <Card className="pv-card">
        <Card.Img variant="top" src={CopyAccountImage} />
        <Card.Body className="p-4">
          <CopyAccountPanels />
        </Card.Body>
      </Card>
    </Col>
  </Row>;
}

export default React.memo(CopyAccountComponent);
